import { Controller } from "@hotwired/stimulus"
import { buildGridGeometry, drawAxisLabels } from "./heatmap_helpers"
import { leadDrill } from "../lead_drill"

// Bucket fills (mirror SCSS legend swatches in _report_tabs.scss).
const BUCKET_FILLS = {
  critical:  "#BE185D",
  under:     "#FDE68A",
  on_target: "#F5F5F5",
  met:       "#7FCDBB",
  over:      "#253494"
}

const BUCKET_TEXT_DARK_FILL = "#ffffff"
const BUCKET_TEXT_LIGHT_FILL = "#1a1a1a"

const DARK_FILL_BUCKETS = new Set(["critical", "over"])

// Glyphs are drawn with canvas paths (NOT unicode glyphs — those break
// across fonts and don't anti-alias predictably in PDF/Grover output).
const GLYPH_DRAW = {
  critical: (ctx, cx, cy, size) => drawTriangle(ctx, cx, cy, size, "down", true),
  under:    (ctx, cx, cy, size) => drawTriangle(ctx, cx, cy, size, "down", false),
  on_target:(ctx, cx, cy, size) => drawDot(ctx, cx, cy, size * 0.25),
  met:      (ctx, cx, cy, size) => drawTriangle(ctx, cx, cy, size, "up",   false),
  over:     (ctx, cx, cy, size) => drawTriangle(ctx, cx, cy, size, "up",   true)
}

const BUCKET_LABELS = {
  critical:  "Critical gap",
  under:     "Understaffed",
  on_target: "On target",
  met:       "Fully staffed",
  over:      "Over-staffed"
}

// LRPO-5159 — the grid's base padding mirrors buildGridGeometry's default. We
// extend ONLY the bottom to reserve a band beneath the grid for the
// setup/teardown caption row (a small ▽ per travel-day column) + its one-line
// legend. Extending the bottom (never the top) leaves the grid's TOP origin —
// and therefore the click hit-testing math in _onCanvasClick — untouched; the
// cells just get marginally shorter.
const GRID_PADDING        = { top: 32, left: 64, right: 16, bottom: 24 }
const SETUP_TEARDOWN_BAND = 40        // px reserved below the grid (▽ caption row + legend)
const TRAVEL_MARKER_COLOR = "#6c757d" // muted (matches the former label color)

function drawTriangle(ctx, cx, cy, size, direction, filled) {
  const half = size / 2
  ctx.beginPath()
  if (direction === "down") {
    ctx.moveTo(cx - half, cy - half / 1.5)
    ctx.lineTo(cx + half, cy - half / 1.5)
    ctx.lineTo(cx,        cy + half)
  } else {
    ctx.moveTo(cx - half, cy + half / 1.5)
    ctx.lineTo(cx + half, cy + half / 1.5)
    ctx.lineTo(cx,        cy - half)
  }
  ctx.closePath()
  if (filled) ctx.fill()
  else ctx.stroke()
}

function drawDot(ctx, cx, cy, r) {
  ctx.beginPath()
  ctx.arc(cx, cy, r, 0, Math.PI * 2)
  ctx.fill()
}

export default class extends Controller {
  static values = {
    payload:  { type: String, default: "{}" },
    view:     { type: String, default: "tiers" },
    canvasId: { type: String, default: "" }
  }

  connect() {
    this._payload = this._parsePayload()
    this._canvas  = this._resolveCanvas()
    if (!this._canvas || !this._payload) return

    this._onResize = this._onResize.bind(this)
    window.addEventListener("resize", this._onResize)

    // Cell-click drilldown (Task 22). Dormant unless the host element
    // carries `data-drill` — the canvas is hand-rolled (no Chart.js), so
    // hit-testing is done by inverse grid math against the cached geometry
    // from the most recent `_render()`. Mirrors the multi-heatmap CELL-only
    // convention: only ACTIVE (non-closed, non-future) cells drill.
    this._onCanvasClick = this._onCanvasClick.bind(this)
    this._canvas.addEventListener("click", this._onCanvasClick)
    if (this.element.dataset.drill) {
      this._canvas.style.cursor = "pointer"
    }

    this._syncToggleButtons(this.viewValue)
    this._render()
  }

  _onResize() {
    // Debounce-ish — animationFrame coalesces resize events
    cancelAnimationFrame(this._resizeRaf)
    this._resizeRaf = requestAnimationFrame(() => this._render())
  }

  switch(event) {
    const mode = event.currentTarget.dataset.mode
    if (mode !== "tiers" && mode !== "numbers") return

    this.viewValue = mode
    this._syncToggleButtons(mode)
    this._render()
  }

  _syncToggleButtons(active) {
    const root = this.element
    root.querySelectorAll("[data-coverage-gap-toggle-btn]").forEach((btn) => {
      const isActive = btn.dataset.mode === active
      btn.classList.toggle("is-active", isActive)
      btn.setAttribute("aria-pressed", isActive)
    })
  }

  disconnect() {
    this._cleanupAriaNodes()
    // Defensive: if the controller is torn down while the popover is open,
    // make sure the document-level keydown listener does not leak.
    document.removeEventListener("keydown", this._popoverKeydown)
    if (this._onResize) window.removeEventListener("resize", this._onResize)
    if (this._resizeRaf) cancelAnimationFrame(this._resizeRaf)
    if (this._canvas && this._onCanvasClick) {
      this._canvas.removeEventListener("click", this._onCanvasClick)
    }
  }

  // -----------------------------------------------------------------
  // Target editor popover
  // -----------------------------------------------------------------

  openTarget(event) {
    event.preventDefault()
    const popover = this._popover()
    if (!popover) return

    popover.classList.remove("d-none")
    popover.setAttribute("aria-hidden", "false")
    this._previouslyFocused = document.activeElement

    // LRPO-5150 — the field is rendered via `fields_for :event_exhibitor`, so
    // its `name` carries a leading bracket (`[event_exhibitor][target_active_scanners]`)
    // and the old hardcoded `name=` selector silently matched null — meaning
    // focus()/select() (and the implicit scroll-into-view they trigger) never
    // ran. On the tall empty-state page the popover then opened below the fold
    // and looked like "nothing happened." Target the explicit data hook instead
    // (with a defensive type=number fallback) so this can't break on a name change.
    const input = popover.querySelector("[data-coverage-gap-target-input]") ||
                  popover.querySelector("input[type='number']")
    if (input) {
      input.focus()
      input.select()
    }
    // Always bring the WHOLE popover (Save/Cancel included) into view — don't
    // rely on focus()'s implicit scroll, which browsers suppress in some
    // scroll-container setups (this page uses a vh-100 / overflow body, where the
    // focused popover was observed staying below the fold at top:950 in a 900px
    // viewport). scrollIntoView({block:'nearest'}) is a no-op when already
    // visible. This is the empty-state fix for LRPO-5150.
    popover.scrollIntoView({ block: "nearest" })

    document.addEventListener("keydown", this._popoverKeydown)
  }

  closeTarget(event) {
    if (event && event.preventDefault) event.preventDefault()
    const popover = this._popover()
    if (!popover) return

    popover.classList.add("d-none")
    popover.setAttribute("aria-hidden", "true")
    document.removeEventListener("keydown", this._popoverKeydown)

    if (this._previouslyFocused && this._previouslyFocused.focus) {
      this._previouslyFocused.focus()
    }
    this._previouslyFocused = null
  }

  _popover() {
    return this.element.querySelector("[data-coverage-gap-target-popover]")
  }

  _popoverKeydown = (e) => {
    if (e.key === "Escape") {
      this.closeTarget()
    }
  }

  _parsePayload() {
    try {
      return JSON.parse(this.payloadValue)
    } catch (_e) {
      return null
    }
  }

  _resolveCanvas() {
    if (this.canvasIdValue) {
      const node = document.getElementById(this.canvasIdValue)
      if (node instanceof HTMLCanvasElement) return node
    }
    return this.element.querySelector("canvas")
  }

  _cleanupAriaNodes() {
    if (!this._ariaContainer) return
    this._ariaContainer.remove()
    this._ariaContainer = null
  }

  _sizeCanvas() {
    const wrapper = this._canvas.parentElement
    if (!wrapper) return
    const rect = wrapper.getBoundingClientRect()
    const dpr = window.devicePixelRatio || 1
    // Round to whole pixels to avoid fractional-px blur
    const cssW = Math.floor(rect.width)
    const cssH = Math.floor(rect.height)
    this._canvas.width  = cssW * dpr
    this._canvas.height = cssH * dpr
    this._canvas.style.width  = `${cssW}px`
    this._canvas.style.height = `${cssH}px`
    // Cache the CSS-pixel dims for the renderer to use
    this._cssWidth  = cssW
    this._cssHeight = cssH
    this._dpr       = dpr
  }

  _render() {
    // LRPO-5152 — defensive guard mirroring multi_heatmap_controller#_buildChart.
    // With no scanner activity the canvas isn't rendered (it lives only in the
    // has_data branch), so _render must no-op rather than deref a null canvas
    // ("Cannot read properties of null (reading 'getContext')"). The view also
    // hides the Tiers/Numbers toggle when has_data is false; this backs up any
    // path that still reaches _render.
    if (!this._canvas) return
    const ctx = this._canvas.getContext("2d")
    const { days, hours, cells, target, target_is_auto } = this._payload
    if (!days || !hours || !cells) return

    this._sizeCanvas()

    // Reset transform then scale to DPR so 1 ctx unit == 1 CSS px
    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.scale(this._dpr, this._dpr)
    ctx.clearRect(0, 0, this._cssWidth, this._cssHeight)

    const geometry = buildGridGeometry({
      canvasWidth:  this._cssWidth,
      canvasHeight: this._cssHeight,
      days,
      hours,
      // LRPO-5159 — reserve a band below the grid for the setup/teardown caption
      // row + legend by growing ONLY the bottom padding. The grid top origin
      // (padding.top) is unchanged, so _onCanvasClick's hit-testing still maps
      // pointer → cell correctly (cellWidth is unaffected; cellHeight shrinks a
      // touch, and the same geometry is reused for both render and hit-testing).
      padding: { ...GRID_PADDING, bottom: GRID_PADDING.bottom + SETUP_TEARDOWN_BAND }
    })

    drawAxisLabels(ctx, { days, hours, geometry })
    this._drawColumnAffordances(ctx, geometry, days)

    // Index cells by [date, hour] for O(1) lookup during render.
    const cellMap = new Map()
    cells.forEach((cell) => {
      cellMap.set(`${cell.date}|${cell.hour}`, cell)
    })

    const todayKey = this._payload.today_key
    const nowHour  = new Date().getHours()
    const isFuture = (cellDate, cellHour) => {
      if (!todayKey) return false
      if (cellDate > todayKey) return true
      if (cellDate === todayKey && cellHour > nowHour) return true
      return false
    }

    days.forEach((day, dayIdx) => {
      hours.forEach((hour) => {
        const rect = geometry.cellRectFor(dayIdx, hour)
        if (!rect) return
        const cell = cellMap.get(`${day.key}|${hour}`)
        if (!cell) {
          this._renderClosedCell(ctx, rect)
        } else if (isFuture(cell.date, cell.hour)) {
          this._renderFutureCell(ctx, rect)
        } else {
          this._renderActiveCell(ctx, rect, cell, target)
        }
      })
    })

    // A11y aria-label nodes get injected once per render.
    this._rebuildAriaLabels({ days, hours, cellMap, target, target_is_auto })

    // Cache the render geometry so the click handler can inverse-map a
    // pointer position → (day, hour) cell without re-deriving the layout.
    this._hitState = { geometry, days, hours, cellMap, isFuture }
  }

  // Matrix CELL click → leadDrill. The canvas is hand-rolled (no Chart.js
  // hit-test seam), so this inverse-maps the pointer's CSS-pixel position
  // against the cached grid geometry. Only ACTIVE cells drill — closed
  // (no scans) and future cells are skipped, matching the multi-heatmap
  // CELL-only convention. Reads `data-drill` on the host element:
  //   data-drill = { origin, anchor, dateParam?, hourParam? }
  // dateParam defaults to "scan_date_exact", hourParam to "scan_hour".
  _onCanvasClick(event) {
    const drill = this.element.dataset.drill
    if (!drill) return
    let cfg
    try {
      cfg = JSON.parse(drill)
    } catch (e) {
      console.warn("coverage-gap-heatmap: data-drill is not valid JSON:", e.message)
      return
    }

    const state = this._hitState
    if (!state) return
    const { geometry, days, hours, cellMap, isFuture } = state

    // Pointer position in CSS pixels (geometry is built in CSS-px units).
    const rect = this._canvas.getBoundingClientRect()
    const px = event.clientX - rect.left
    const py = event.clientY - rect.top

    const { padding, cellWidth, cellHeight } = geometry
    if (cellWidth <= 0 || cellHeight <= 0) return

    const dayIdx  = Math.floor((px - padding.left) / cellWidth)
    const hourIdx = Math.floor((py - padding.top)  / cellHeight)
    if (dayIdx < 0 || dayIdx >= days.length) return
    if (hourIdx < 0 || hourIdx >= hours.length) return

    const day  = days[dayIdx]
    const hour = hours[hourIdx]
    const cell = cellMap.get(`${day.key}|${hour}`)
    // Skip closed (no cell) and future cells — nothing to drill into.
    if (!cell || isFuture(cell.date, cell.hour)) return

    const filters = {}
    const dateParam = cfg.dateParam === undefined ? "scan_date_exact" : cfg.dateParam
    const hourParam = cfg.hourParam === undefined ? "scan_hour" : cfg.hourParam
    if (dateParam && cell.date != null) filters[dateParam] = cell.date
    if (hourParam && cell.hour != null) filters[hourParam] = cell.hour
    if (!Object.keys(filters).length) return

    leadDrill(filters, { origin: cfg.origin, anchor: cfg.anchor })
  }

  _drawColumnAffordances(ctx, geometry, days) {
    const travel = new Set(this._payload.travel_day_keys || [])
    const todayKey = this._payload.today_key

    // LRPO-5159 — the setup/teardown affordance lives in a CAPTION ROW below the
    // grid (not the cramped header zone, where the old wide "Setup/Teardown"
    // text overlapped neighboring columns + the day headers at high day-column
    // density). gridBottom is the lower edge of the last hour row, which equals
    // (canvasHeight - padding.bottom) by construction; the reserved band
    // (SETUP_TEARDOWN_BAND) sits between it and the canvas edge.
    const gridBottom = this._cssHeight - geometry.padding.bottom
    const markerY = gridBottom + 14 // ▽ marker, just below the grid
    const legendY = gridBottom + 30 // one-line legend, below the markers

    days.forEach((day, dayIdx) => {
      const colX = geometry.padding.left + dayIdx * geometry.cellWidth

      // Travel-day marker: a small centered ▽ in the caption row below the grid.
      // A single narrow glyph per column fits even at ~67-column density (unlike
      // the wide text it replaces). Drawn as a canvas path (not a unicode glyph)
      // for predictable anti-aliasing in PDF/Grover output — same convention as
      // the cell glyphs above.
      if (travel.has(day.key)) {
        ctx.save()
        ctx.strokeStyle = TRAVEL_MARKER_COLOR
        ctx.lineWidth = 1
        drawTriangle(ctx, colX + geometry.cellWidth / 2, markerY, 10, "down", false)
        ctx.restore()
      }

      // Today's column dotted top border
      if (day.key === todayKey) {
        ctx.save()
        ctx.strokeStyle = "#999999"
        ctx.lineWidth = 1
        ctx.setLineDash([2, 2])
        ctx.beginPath()
        ctx.moveTo(colX, geometry.padding.top)
        ctx.lineTo(colX + geometry.cellWidth, geometry.padding.top)
        ctx.stroke()
        ctx.setLineDash([])
        ctx.restore()
      }
    })

    // Single legend entry for the travel-day marker — only when the event has
    // travel days. A ▽ glyph + "Setup/teardown day", left-aligned under the
    // hour-axis gutter, below the marker caption row.
    if (travel.size > 0) {
      ctx.save()
      ctx.strokeStyle = TRAVEL_MARKER_COLOR
      ctx.fillStyle = TRAVEL_MARKER_COLOR
      ctx.lineWidth = 1
      const glyphX = geometry.padding.left
      drawTriangle(ctx, glyphX, legendY, 9, "down", false)
      ctx.font = "10px system-ui, sans-serif"
      ctx.textAlign = "left"
      ctx.textBaseline = "middle"
      ctx.fillText("Setup/teardown day", glyphX + 9, legendY)
      ctx.restore()
    }
  }

  _renderClosedCell(ctx, rect) {
    ctx.save()
    ctx.fillStyle = "#ffffff"
    ctx.fillRect(rect.x, rect.y, rect.w, rect.h)

    ctx.strokeStyle = "#cbd5e1"
    ctx.lineWidth = 1
    // Diagonal hatch
    const spacing = 6
    ctx.beginPath()
    for (let d = -rect.h; d < rect.w; d += spacing) {
      ctx.moveTo(rect.x + d,            rect.y)
      ctx.lineTo(rect.x + d + rect.h,   rect.y + rect.h)
    }
    ctx.stroke()
    ctx.restore()
  }

  _renderFutureCell(ctx, rect) {
    ctx.save()
    ctx.fillStyle = "#ffffff"
    ctx.fillRect(rect.x, rect.y, rect.w, rect.h)
    ctx.strokeStyle = "#e0e0e0"
    ctx.lineWidth = 1
    ctx.strokeRect(rect.x + 0.5, rect.y + 0.5, rect.w - 1, rect.h - 1)
    ctx.restore()
  }

  _renderActiveCell(ctx, rect, cell, target) {
    const fill = BUCKET_FILLS[cell.bucket]
    ctx.save()
    ctx.fillStyle = fill
    ctx.fillRect(rect.x, rect.y, rect.w, rect.h)

    // Critical gap hairline overlay (1px diagonal, 15% black, 6° angle simulated).
    if (cell.bucket === "critical") {
      ctx.strokeStyle = "rgba(0,0,0,0.15)"
      ctx.lineWidth = 1
      const spacing = 4
      ctx.beginPath()
      for (let d = -rect.h; d < rect.w; d += spacing) {
        ctx.moveTo(rect.x + d,          rect.y)
        ctx.lineTo(rect.x + d + rect.h, rect.y + rect.h)
      }
      ctx.stroke()
    }

    if (this.viewValue === "numbers") {
      this._drawNumbersCellContent(ctx, rect, cell)
    } else {
      this._drawTiersGlyph(ctx, rect, cell)
    }

    ctx.restore()
  }

  _drawTiersGlyph(ctx, rect, cell) {
    const glyphSize = Math.min(rect.w, rect.h) * 0.5
    const cx = rect.x + rect.w / 2
    const cy = rect.y + rect.h / 2
    const draw = GLYPH_DRAW[cell.bucket]
    if (!draw) return
    const useDark = DARK_FILL_BUCKETS.has(cell.bucket)
    ctx.fillStyle   = useDark ? BUCKET_TEXT_DARK_FILL : BUCKET_TEXT_LIGHT_FILL
    ctx.strokeStyle = useDark ? BUCKET_TEXT_DARK_FILL : BUCKET_TEXT_LIGHT_FILL
    ctx.lineWidth   = 1.5
    draw(ctx, cx, cy, glyphSize)
  }

  _drawNumbersCellContent(ctx, rect, cell) {
    const useDark  = DARK_FILL_BUCKETS.has(cell.bucket)
    const textFill = useDark ? BUCKET_TEXT_DARK_FILL : BUCKET_TEXT_LIGHT_FILL
    const cx = rect.x + rect.w / 2
    const cy = rect.y + rect.h / 2

    ctx.fillStyle    = textFill
    ctx.textAlign    = "center"
    ctx.textBaseline = "middle"
    ctx.font         = "bold 14px system-ui, sans-serif"
    ctx.fillText(String(cell.active_count), cx, cy - 2)

    const showSubscript = ["critical", "under", "on_target"].includes(cell.bucket)
    if (showSubscript) {
      const pct = Math.round((cell.pct || 0) * 100)
      ctx.font = "9px system-ui, sans-serif"
      ctx.fillText(`${pct}%`, cx, cy + 12)
    }
  }

  _rebuildAriaLabels({ days, hours, cellMap, target, target_is_auto }) {
    this._cleanupAriaNodes()
    const container = document.createElement("div")
    container.className = "visually-hidden"
    container.setAttribute("aria-live", "off")

    days.forEach((day) => {
      hours.forEach((hour) => {
        const cell = cellMap.get(`${day.key}|${hour}`)
        if (!cell) return
        const node = document.createElement("span")
        node.textContent = this._ariaLabelFor(day, hour, cell, target)
        container.appendChild(node)
      })
    })

    this._canvas.parentElement.appendChild(container)
    this._ariaContainer = container
  }

  _ariaLabelFor(day, hour, cell, target) {
    const todayKey = this._payload.today_key
    const nowHour  = new Date().getHours()
    const isFuture = todayKey && (cell.date > todayKey || (cell.date === todayKey && cell.hour > nowHour))
    if (isFuture) return `${day.day_num} ${this._hourLabel(hour)}, future`
    const pct = Math.round((cell.pct || 0) * 100)
    return `${day.day_num} ${day.day_date} ${this._hourLabel(hour)}, ${cell.active_count} of ${target} scanners active, ${pct}%, ${BUCKET_LABELS[cell.bucket]}`
  }

  _hourLabel(hour) {
    if (hour === 0) return "12 AM"
    if (hour === 12) return "12 PM"
    if (hour < 12) return `${hour} AM`
    return `${hour - 12} PM`
  }
}
